import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Collapse } from 'antd';
import PropertyList from './PropertyList';
import { PropertyFull } from "../../types";

const { Panel } = Collapse;

interface GroupedProperties {
    typeName: string;
    typeId: string;
    properties: PropertyFull[];
}

interface GroupedPropertiesListProps {
    properties: PropertyFull[];
    selectedProperty?: PropertyFull;
    actions: (property: PropertyFull, isSelected: boolean) => React.ReactNode[];
    showNextButton?: boolean;
    /** Optional renderer for extra controls in each group's panel header (e.g. Add button) */
    groupHeaderExtra?: (typeId: string, typeName: string) => React.ReactNode;
}

const GroupedPropertiesList: React.FC<GroupedPropertiesListProps> = ({
                                                                         properties,
                                                                         selectedProperty,
                                                                         actions,
                                                                         showNextButton = false,
                                                                         groupHeaderExtra,
                                                                     }) => {
    // Группируем свойства по типу
    const groupedProperties: GroupedProperties[] = useMemo(() => {
        const groups: Record<string, GroupedProperties> = {};
        properties.forEach((prop) => {
            const typeName = prop.type?.name || 'Неопределённые';
            const typeId = prop.propertyTypeId || '';
            if (!groups[typeName]) {
                groups[typeName] = { typeName, typeId, properties: [] };
            }
            groups[typeName].properties.push(prop);
        });
        return Object.values(groups);
    }, [properties]);

    // Состояние для управления открытыми панелями
    const [activePanels, setActivePanels] = useState<string[]>([]);
    const handleCollapseChange = useCallback((activeKeys: string | string[]) => {
        const keys = Array.isArray(activeKeys) ? activeKeys : [activeKeys];
        setActivePanels(keys);
    }, []);

    useEffect(() => {
        const keys = groupedProperties.map((group) => group.typeName);
        setActivePanels(keys);
    }, [groupedProperties]);

    // Флаг, что содержимое панелей уже предварительно отрендерено в фоне
    const [preRendered, setPreRendered] = useState<boolean>(false);
    useEffect(() => {
        // Запускаем pre-render через небольшой delay после монтирования страницы
        const timeout = setTimeout(() => {
            setPreRendered(true);
        }, 40); // 100 мс можно настроить под нужды
        return () => clearTimeout(timeout);
    }, []);

    return (
        <div style={{ position: 'relative', paddingBottom: '60px' }}>
            <Collapse activeKey={activePanels} onChange={handleCollapseChange}>
                {groupedProperties.map((group) => {
                    const isActive = activePanels.includes(group.typeName);
                    const extra = groupHeaderExtra
                        ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                {groupHeaderExtra(group.typeId, group.typeName)}
                            </span>
                        )
                        : undefined;
                    return (
                        <Panel header={group.typeName} key={group.typeName} extra={extra}>
                            {preRendered ? (
                                // Если панель закрыта, содержимое рендерится, но скрыто (display: none)
                                <div style={{ display: isActive ? 'block' : 'none' }}>
                                    <PropertyList
                                        properties={group.properties}
                                        selectedProperty={selectedProperty}
                                        actions={actions}
                                    />
                                </div>
                            ) : (
                                // Пока pre-render не завершён, выводим плейсхолдер
                                <div style={{ minHeight: '100px' }} />
                            )}
                        </Panel>
                    );
                })}
            </Collapse>
            {showNextButton && (
                <div>
                    {/* Реализуйте логику кнопки перехода к следующей панели, если требуется */}
                </div>
            )}
        </div>
    );
};

export default React.memo(GroupedPropertiesList);
